import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaChevronDown, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Pagination from "../../../components/Pagination/Pagination";
import { API } from "aws-amplify";
import Moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DatePickerPopperContainer from "../../../components/DatePickerPopperContainer";
import "../../../styles/compact-filters.css";
import "../../../styles/ops-list.css";
import PageHeader from "../../../components/PageHeader/PageHeader";
import { useI18n } from "../../../i18n";
import * as localdb from "../../../localdb";
import "./TaskList.css";

const FILTERS_KEY = "taskListFilters";
const QUICK_KEY = "taskListQuickFilter";
const SORT_KEY = "taskListSort";
const PAGE_SIZE_KEY = "taskListPageSize";
const PAGE_SIZES = [10, 25, 50];
const STATUS_OPTIONS = ["Created", "Accepted", "WIP", "Started", "Completed"];
const RECENT_MS = 7 * 24 * 60 * 60 * 1000;

const apiParams = {
	headers: {
		"x-api-key": process.env.REACT_APP_API_KEY,
		Accept: "application/json",
		"Content-Type": "application/json",
	},
};

function readStored(key, fallback) {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : fallback;
	} catch (e) {
		return fallback;
	}
}

function statusClass(status) {
	switch (status) {
		case "Created":
			return "task_created";
		case "Accepted":
			return "task_accepted";
		case "Started":
			return "task_started";
		case "Completed":
			return "task_completed";
		case "WIP":
			return "task_wip";
		default:
			return "taskStatus";
	}
}

function slaUnit(value, category) {
	const text = String(value || "");
	if (/days/i.test(text)) return "days";
	if (/hrs|hours/i.test(text)) return "hrs";
	return category === "Reactive" ? "hrs" : "days";
}

function slaDurationMs(value, category) {
	const amount = parseFloat(value);
	if (!Number.isFinite(amount) || amount < 0) return null;
	return slaUnit(value, category) === "days" ? amount * 86400000 : amount * 3600000;
}

function formatSla(value, category) {
	if (value === undefined || value === null || value === "") return null;
	const text = String(value).trim();
	if (/hrs|days/i.test(text)) return text;
	return `${text} ${slaUnit(value, category)}`;
}

function formatDuration(ms, t) {
	const abs = Math.abs(ms);
	if (abs < 3600000) return t('time.minutesShort', { n: Math.max(1, Math.round(abs / 60000)) });
	if (abs < 48 * 3600000) return t('time.hoursShort', { n: Math.round(abs / 3600000) });
	return t('time.daysShort', { n: Math.round(abs / 86400000) });
}

function getSlaView(item, now, t) {
	const contract = formatSla(item.sla_attend, item.category);
	const windowMs = slaDurationMs(item.sla_attend, item.category);
	const created = item.date_created ? new Date(item.date_created).getTime() : NaN;
	if (!windowMs || !Number.isFinite(created)) {
		return { tone: "ok", label: contract ? t('tasks.slaAttendValue', { contract }) : null, remainingMs: null, contract };
	}
	const remainingMs = created + windowMs - now;
	if (remainingMs <= 0) {
		return { tone: "overdue", label: t('tasks.overdueBy', { duration: formatDuration(remainingMs, t) }), remainingMs, contract };
	}
	const soonMs = Math.min(windowMs * 0.25, slaUnit(item.sla_attend, item.category) === "days" ? 86400000 : 3600000);
	return {
		tone: remainingMs <= soonMs ? "soon" : "ok",
		label: t('tasks.timeLeft', { duration: formatDuration(remainingMs, t) }),
		remainingMs,
		contract,
	};
}

function formatCreated(value) {
	if (!value) return "";
	const m = Moment(value);
	return m.isValid() ? m.format("D MMM YYYY, HH:mm") : String(value);
}

function isUrgentItem(item) {
	return String(item.urgent_fault || "").toLowerCase() === "urgent";
}

function readPageSize() {
	const n = parseInt(localStorage.getItem(PAGE_SIZE_KEY), 10);
	return PAGE_SIZES.includes(n) ? n : 10;
}

export default function TaskList(props) {
	const { t, tStatus, tCatalog } = useI18n();
	const savedFilters = readStored(FILTERS_KEY, {}) || {};
	const [taskList, setTaskList] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(readPageSize);
	const [expandedId, setExpandedId] = useState(null);
	const [showMoreFilters, setShowMoreFilters] = useState(
		Boolean(savedFilters.subcategory || savedFilters.dateFrom || savedFilters.dateTo || savedFilters.taskRef || savedFilters.siteRef)
	);
	const [quickFilter, setQuickFilter] = useState(() => localStorage.getItem(QUICK_KEY) || "all");
	const [sortBy, setSortBy] = useState(() => localStorage.getItem(SORT_KEY) || "created_desc");
	const [search, setSearch] = useState(savedFilters.search || "");
	const [taskRef, setTaskRef] = useState(savedFilters.taskRef || "");
	const [siteRef, setSiteRef] = useState(savedFilters.siteRef || "");
	const [district, setDistrict] = useState(savedFilters.district || "");
	const [category, setCategory] = useState(savedFilters.category || "");
	const [subcategory, setSubcategory] = useState(savedFilters.subcategory || "");
	const [status, setStatus] = useState(savedFilters.status || "");
	const [dateFrom, setDateFrom] = useState(savedFilters.dateFrom ? new Date(savedFilters.dateFrom) : null);
	const [dateTo, setDateTo] = useState(savedFilters.dateTo ? new Date(savedFilters.dateTo) : null);
	const [districtList, setDistrictList] = useState([]);

	useEffect(() => {
		fetchTasks();
		fetchDistricts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		localStorage.setItem(
			FILTERS_KEY,
			JSON.stringify({
				taskRef,
				siteRef,
				district,
				category,
				subcategory,
				status,
				search,
				dateFrom: dateFrom ? dateFrom.toISOString() : null,
				dateTo: dateTo ? dateTo.toISOString() : null,
			})
		);
	}, [taskRef, siteRef, district, category, subcategory, status, search, dateFrom, dateTo]);

	useEffect(() => {
		localStorage.setItem(QUICK_KEY, quickFilter);
	}, [quickFilter]);

	useEffect(() => {
		localStorage.setItem(SORT_KEY, sortBy);
	}, [sortBy]);

	useEffect(() => {
		localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
		setCurrentPage(1);
	}, [pageSize]);

	const fetchDistricts = async () => {
		try {
			const districts = await API.get("settings", "/DISTRICTS", apiParams);
			const unique = [...new Set((districts.values || []).filter(Boolean))];
			setDistrictList(unique);
		} catch (e) {
			setDistrictList([]);
		}
	};

	const fetchTasks = async () => {
		setIsLoading(true);
		try {
			const response = await API.get("task_active_all", "/", apiParams);
			const items = (response || []).map((item) => ({
				...item,
				cssTaskStatus: statusClass(item.task_status),
			}));
			setTaskList(items);
			setCurrentPage(1);
			setExpandedId(null);
		} catch (e) {
			alert(e);
		}
		setIsLoading(false);
	};

	const handleReset = () => {
		setTaskRef("");
		setSiteRef("");
		setDistrict("");
		setCategory("");
		setSubcategory("");
		setStatus("");
		setSearch("");
		setDateFrom(null);
		setDateTo(null);
		setQuickFilter("all");
		setSortBy("created_desc");
		setCurrentPage(1);
		setExpandedId(null);
		localStorage.removeItem(FILTERS_KEY);
		localStorage.removeItem(QUICK_KEY);
		localStorage.removeItem(SORT_KEY);
	};

	const filteredList = useMemo(() => {
		const query = search.trim().toLowerCase();
		const user = (localStorage.getItem("user") || "").toLowerCase();
		const contractor = (localStorage.getItem("contractor") || "").toLowerCase();
		const now = Date.now();

		let list = taskList.filter((item) => {
			if (taskRef && !(item.task_ref && String(item.task_ref).includes(String(taskRef)))) return false;
			if (siteRef && !(item.site_ref && item.site_ref.toLowerCase().includes(siteRef.toLowerCase()))) return false;
			if (district && !(item.district && item.district.includes(district))) return false;
			if (category && !(item.category && item.category.includes(category))) return false;
			if (subcategory && !(item.type_fault && item.type_fault.includes(subcategory))) return false;
			if (dateFrom) {
				const fromIso = new Date(dateFrom).toISOString();
				if (!item.date_created || item.date_created < fromIso) return false;
			}
			if (dateTo) {
				const to = new Date(dateTo);
				to.setHours(23, 59, 59, 999);
				if (!item.date_created || item.date_created > to.toISOString()) return false;
			}
			if (status && String(item.task_status || "") !== status) return false;
			if (quickFilter === "urgent" && !isUrgentItem(item)) return false;
			if (quickFilter === "recent") {
				const created = item.date_created ? new Date(item.date_created).getTime() : 0;
				if (!created || now - created > RECENT_MS) return false;
			}
			if (quickFilter === "assigned") {
				const createdBy = String(item.user_created || "").toLowerCase();
				const itemContractor = String(item.contractor || "").toLowerCase();
				const matchesUser = user && createdBy && createdBy === user;
				const matchesContractor = contractor && itemContractor && itemContractor === contractor;
				if (!matchesUser && !matchesContractor) return false;
			}
			if (query) {
				const haystack = [item.task_ref, item.site_ref, item.site_address, item.fault_description, item.district]
					.join(" ")
					.toLowerCase();
				if (!haystack.includes(query)) return false;
			}
			return true;
		});

		const urgencyRank = (item) => (isUrgentItem(item) ? 0 : 1);
		const statusRank = (item) => {
			const index = STATUS_OPTIONS.indexOf(item.task_status);
			return index === -1 ? 99 : index;
		};
		const slaValue = (item) => {
			const remaining = getSlaView(item, now, t).remainingMs;
			if (remaining !== null) return remaining;
			const raw = parseFloat(item.sla_attend);
			return Number.isFinite(raw) ? raw : Number.POSITIVE_INFINITY;
		};

		return [...list].sort((a, b) => {
			if (sortBy === "created_asc") {
				return new Date(a.date_created || 0) - new Date(b.date_created || 0);
			}
			if (sortBy === "urgency") {
				const urg = urgencyRank(a) - urgencyRank(b);
				if (urg !== 0) return urg;
				return new Date(b.date_created || 0) - new Date(a.date_created || 0);
			}
			if (sortBy === "status") {
				return statusRank(a) - statusRank(b);
			}
			if (sortBy === "sla") {
				return slaValue(a) - slaValue(b);
			}
			return new Date(b.date_created || 0) - new Date(a.date_created || 0);
		});
	}, [taskList, search, status, quickFilter, sortBy, taskRef, siteRef, district, category, subcategory, dateFrom, dateTo, t]);

	useEffect(() => {
		const pages = Math.max(1, Math.ceil(filteredList.length / pageSize));
		if (currentPage > pages) setCurrentPage(pages);
	}, [filteredList.length, currentPage, pageSize]);

	const indexOfLastItem = currentPage * pageSize;
	const indexOfFirstItem = indexOfLastItem - pageSize;
	const currentItemList = filteredList.slice(indexOfFirstItem, indexOfLastItem);
	const rangeStart = filteredList.length === 0 ? 0 : indexOfFirstItem + 1;
	const rangeEnd = Math.min(indexOfLastItem, filteredList.length);

	const paginate = (pageNumber) => {
		setCurrentPage(pageNumber);
		setExpandedId(null);
	};

	const toggleExpand = (taskId) => {
		setExpandedId((current) => (current === taskId ? null : taskId));
	};

	const categoryList = localdb.CATEGORY.map((item, i) => (
		<option key={"category" + i} value={item.key}>
			{tCatalog(item.description)}
		</option>
	));

	const selectedCategory = localdb.CATEGORY.find((item) => item.key === category);
	const subcategoryList = (selectedCategory && selectedCategory.subcategory) || [];

	const statusList = Array.from(
		new Set([...STATUS_OPTIONS, ...taskList.map((item) => item.task_status).filter(Boolean)])
	);

	const chips = [
		{ id: "all", label: t('tasks.allTasks') },
		{ id: "urgent", label: t('status.urgent') },
		{ id: "recent", label: t('tasks.recentlyCreated') },
		{ id: "assigned", label: t('tasks.assignedToMe') },
	];

	const renderRow = (item) => {
		const open = expandedId === item.taskId;
		const description = item.fault_description || t('tasks.noDescription');
		const slaAttend = formatSla(item.sla_attend, item.category);
		const slaRepair = formatSla(item.sla_repair, item.category);
		const slaView = getSlaView(item, Date.now(), t);
		const createdLabel = formatCreated(item.date_created);
		const categoryLine = [tCatalog(item.category), tCatalog(item.type_fault)].filter(Boolean).join(" · ");
		const siteName = (item.site_address || "").trim();
		const placeName = [siteName, item.district].filter(Boolean).join(" · ");

		return (
			<article
				key={item.taskId}
				className={`task-ops-item${open ? " is-open" : ""}${isUrgentItem(item) ? " is-urgent" : ""}`}
				onClick={() => toggleExpand(item.taskId)}
			>
				<div
					className="task-ops-row"
					role="button"
					tabIndex={0}
					aria-expanded={open}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							toggleExpand(item.taskId);
						}
					}}
				>
					<div className="task-ops-identity">
						<Link
							className="task-ops-id"
							to={`/task_det/${item.taskId}`}
							onClick={(e) => e.stopPropagation()}
						>
							#{item.task_ref}
						</Link>
						{item.task_status && (
							<span className={`task-ops-status ${item.cssTaskStatus}`}>{tStatus(item.task_status)}</span>
						)}
						{isUrgentItem(item) && (
							<span className="task-ops-flag" title={t('status.urgent')}>
								<span className="task-ops-flag-dot" aria-hidden="true" />
								{t('status.urgent')}
							</span>
						)}
						{createdLabel && <span className="task-ops-date">{createdLabel}</span>}
					</div>
					<button
						type="button"
						className="task-ops-toggle"
						aria-label={open ? t('tasks.collapse') : t('tasks.expand')}
						onClick={(e) => {
							e.stopPropagation();
							toggleExpand(item.taskId);
						}}
					>
						{open ? <FaChevronDown /> : <FaChevronRight />}
					</button>
					<p className="task-ops-title" title={description}>
						{description}
					</p>
					<div className="task-ops-meta">
						<div className="task-ops-place">
							{item.site_ref && <span className="task-ops-site-code">{item.site_ref}</span>}
							{placeName && (
								<span className="task-ops-site-name" title={placeName}>
									{placeName}
								</span>
							)}
							{categoryLine && <span className="task-ops-cat">· {categoryLine}</span>}
						</div>
						{slaView.label && (
							<div className="task-ops-sla-wrap">
								<span className={`task-ops-sla is-${slaView.tone}`}>{slaView.label}</span>
							</div>
						)}
					</div>
				</div>
				{open && (
					<div className="task-ops-detail">
						<div className="task-ops-detail-inner">
							<dl>
								<div className="is-wide">
									<dt>{t('common.address')}</dt>
									<dd>
										{item.site_address || "—"}
										{item.district ? ` · ${item.district}` : ""}
									</dd>
								</div>
								<div className="is-wide">
									<dt>{t('common.description')}</dt>
									<dd>{description}</dd>
								</div>
								<div>
									<dt>{t('common.contractor')}</dt>
									<dd>{item.contractor || "—"}</dd>
								</div>
								<div>
									<dt>{t('common.classification')}</dt>
									<dd>{item.urgent_fault || "—"}</dd>
								</div>
								<div>
									<dt>{t('tasks.slaAttend')}</dt>
									<dd>{slaAttend || "—"}</dd>
								</div>
								<div>
									<dt>{t('tasks.slaRepair')}</dt>
									<dd>{slaRepair || "—"}</dd>
								</div>
							</dl>
							<div className="task-ops-actions">
								<Link className="task-ops-open" to={`/task_det/${item.taskId}`} onClick={(e) => e.stopPropagation()}>
									<FaExternalLinkAlt /> {t('tasks.openTask')}
								</Link>
							</div>
						</div>
					</div>
				)}
			</article>
		);
	};

	return (
		<div className="TaskList">
			<div>
				<PageHeader
					title={t('pages.tasksActive.title')}
					subtitle={t('pages.tasksActive.subtitle')}
					breadcrumbs={[{ label: t('nav.tasks'), path: "/tasks" }]}
					count={!isLoading ? filteredList.length : undefined}
					countLabel={t('common.tasks')}
					actions={
						<Button
							size="sm"
							variant="success"
							onClick={() => {
								props.history.push("/task_add/");
							}}
						>
							<FaPlus /> {t('tasks.addTask')}
						</Button>
					}
				/>
				<div className="task-ops">
					<div className="task-ops-toolbar">
						<div className="task-ops-chips" role="tablist" aria-label={t('tasks.quickFilters')}>
							{chips.map((chip) => (
								<button
									key={chip.id}
									type="button"
									role="tab"
									aria-selected={quickFilter === chip.id}
									className={`task-ops-chip${quickFilter === chip.id ? " is-active" : ""}`}
									onClick={() => {
										setQuickFilter(chip.id);
										setCurrentPage(1);
									}}
								>
									{chip.label}
								</button>
							))}
						</div>
						<div className="task-ops-sort">
							<label htmlFor="task-ops-sort">{t('tasks.sort')}</label>
							<select
								id="task-ops-sort"
								value={sortBy}
								onChange={(e) => {
									setSortBy(e.target.value);
									setCurrentPage(1);
								}}
							>
								<option value="created_desc">{t('tasks.newestCreated')}</option>
								<option value="created_asc">{t('tasks.oldestCreated')}</option>
								<option value="urgency">{t('tasks.urgency')}</option>
								<option value="status">{t('common.status')}</option>
								<option value="sla">{t('tasks.slaAttend')}</option>
							</select>
						</div>
					</div>

					<Form
						className="compact-filters task-ops-filters"
						onSubmit={(e) => e.preventDefault()}
					>
						<div className="task-ops-filters-main">
							<Form.Control
								className="task-ops-search"
								size="sm"
								placeholder={t('tasks.searchPlaceholder')}
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
									setCurrentPage(1);
								}}
							/>
							<Form.Control
								as="select"
								size="sm"
								value={status}
								onChange={(e) => {
									setStatus(e.target.value);
									setCurrentPage(1);
								}}
							>
								<option value="">{t('common.status')}</option>
								{statusList.map((item) => (
									<option key={item} value={item}>
										{tStatus(item)}
									</option>
								))}
							</Form.Control>
							<Form.Control
								as="select"
								size="sm"
								value={district}
								onChange={(e) => {
									setDistrict(e.target.value);
									setCurrentPage(1);
								}}
							>
								<option value="">{t('common.district')}</option>
								{districtList.map((item) => (
									<option key={item} value={item}>
										{item}
									</option>
								))}
							</Form.Control>
							<Form.Control
								as="select"
								size="sm"
								value={category}
								onChange={(e) => {
									setCategory(e.target.value);
									setSubcategory("");
									setCurrentPage(1);
								}}
							>
								<option value="">{t('common.category')}</option>
								{categoryList}
							</Form.Control>
							<button
								type="button"
								className={`task-ops-more${showMoreFilters ? " is-open" : ""}`}
								aria-expanded={showMoreFilters}
								onClick={() => setShowMoreFilters((v) => !v)}
							>
								{t('common.moreFilters')}
							</button>
							<button type="button" className="task-ops-reset" onClick={handleReset}>
								{t('common.reset')}
							</button>
						</div>
						{showMoreFilters && (
							<div className="task-ops-filters-panel">
								<Form.Control
									size="sm"
									placeholder={t('tasks.taskRef')}
									value={taskRef}
									onChange={(e) => {
										setTaskRef(e.target.value);
										setCurrentPage(1);
									}}
								/>
								<Form.Control
									size="sm"
									placeholder={t('tasks.siteRef')}
									value={siteRef}
									onChange={(e) => {
										setSiteRef(e.target.value);
										setCurrentPage(1);
									}}
								/>
								<Form.Control
									as="select"
									size="sm"
									value={subcategory}
									onChange={(e) => {
										setSubcategory(e.target.value);
										setCurrentPage(1);
									}}
									disabled={!category}
								>
									<option value="">{t('common.subcategory')}</option>
									{subcategoryList.map((item) => (
										<option key={item} value={item}>
											{tCatalog(item)}
										</option>
									))}
								</Form.Control>
								<DatePicker
									selected={dateFrom}
									onChange={(date) => {
										setDateFrom(date);
										setCurrentPage(1);
									}}
									className="form-control form-control-sm"
									placeholderText={t('common.from')}
									dateFormat="yyyy-MM-dd"
									isClearable
									popperContainer={DatePickerPopperContainer}
								/>
								<DatePicker
									selected={dateTo}
									onChange={(date) => {
										setDateTo(date);
										setCurrentPage(1);
									}}
									className="form-control form-control-sm"
									placeholderText={t('common.to')}
									dateFormat="yyyy-MM-dd"
									isClearable
									popperContainer={DatePickerPopperContainer}
								/>
							</div>
						)}
					</Form>

					<div className="task-ops-panel" aria-busy={isLoading}>
						{isLoading ? (
							<div className="loading-container">
								<div className="loading-content">
									<Spinner animation="border" role="status" variant="primary" className="loading-spinner" />
									<div className="loading-text">{t('tasks.loading')}</div>
								</div>
							</div>
						) : (
							<>
								<div className="task-ops-legend" aria-hidden="true">
									{t('tasks.legendActive')}
								</div>
								<div className="task-ops-list">
									{currentItemList.length === 0 ? (
										<div className="task-ops-empty">{t('tasks.emptyActive')}</div>
									) : (
										currentItemList.map(renderRow)
									)}
								</div>
								<div className="task-ops-footer">
									<p className="task-ops-range">
										{t('tasks.showing', { start: rangeStart, end: rangeEnd, total: filteredList.length })}
									</p>
									<div className="task-ops-pagesize">
										<label htmlFor="task-ops-pagesize">{t('common.rows')}</label>
										<select
											id="task-ops-pagesize"
											value={pageSize}
											onChange={(e) => setPageSize(Number(e.target.value))}
										>
											{PAGE_SIZES.map((size) => (
												<option key={size} value={size}>
													{size}
												</option>
											))}
										</select>
									</div>
									<Pagination
										sizePerPage={pageSize}
										currentPage={currentPage}
										totalSize={filteredList.length}
										changeCurrentPage={paginate}
										theme="bootstrap"
									/>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
